/**
 * CosignNotesTab
 *
 * Co-sign notes queue: session notes and chart documents (treatment plans, etc.)
 * awaiting supervisor co-signature.
 */

import { Alert, Group, Loader, Paper, Stack } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SupervisionEmptyState } from '../components/SupervisionEmptyState';
import { SupervisionFilters } from '../components/SupervisionFilters';
import { TablePaginationFooter } from '@/components/TablePaginationFooter';
import { CosignNotesTable } from '../components/CosignNotesTable';
import { useCosignQueueFromMedplum } from '../hooks/useCosignQueueFromMedplum';
import { useSupervisionQueue } from '../hooks/useSupervisionQueue';
import { useSupervisionScope } from '../hooks/useSupervisionScope';
import type {
  SupervisionFilters as SupervisionFiltersType,
  SupervisionQueueItemWithDocumentId,
} from '../types';
import { defaultSupervisionFilters } from '../types';
import type { SupervisionNotesQueue } from '../utils/questionnaireResponseMappings';
import { OWN_UNSIGNED_DOCUMENT_TYPES } from '../utils/questionnaireResponseMappings';

type SortColumn = SupervisionFiltersType['sortBy'];

export function CosignNotesTab({
  queue = 'role-default',
}: {
  queue?: SupervisionNotesQueue;
}): JSX.Element {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<SupervisionFiltersType>(defaultSupervisionFilters);
  const isOwnUnsigned = queue === 'own-unsigned';
  const { role, superviseeOptions } = useSupervisionScope();
  const unsignedEmpty = isOwnUnsigned || role === 'provider';

  const { items: queueItemsWithDocumentId, loading, error, refetch } = useCosignQueueFromMedplum({
    queue,
  });

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const filteredItems = useSupervisionQueue(queueItemsWithDocumentId, filters);
  const unsignedCount = queueItemsWithDocumentId.length;
  const clinicianOptions = useMemo(() => {
    const byId = new Map<string, string>();
    if (!isOwnUnsigned && role === 'supervisor') {
      for (const option of superviseeOptions) {
        byId.set(option.id, option.name);
      }
    }
    for (const item of queueItemsWithDocumentId) {
      if (item.clinicianId && item.clinicianName && !byId.has(item.clinicianId)) {
        byId.set(item.clinicianId, item.clinicianName);
      }
    }
    return [...byId.entries()].map(([value, label]) => ({ value, label }));
  }, [queueItemsWithDocumentId, isOwnUnsigned, role, superviseeOptions]);

  const handleItemClick = useCallback(
    (item: SupervisionQueueItemWithDocumentId) => {
      if (item.documentIdForReview) {
        navigate(`/dashboard/supervision/review/${item.documentIdForReview}`);
        return;
      }
      if (item.appointmentId) {
        navigate(`/dashboard/appointments/${item.appointmentId}`);
      }
    },
    [navigate]
  );

  const handleSort = useCallback(
    (column: SortColumn) => {
      setFilters((prev) => {
        const isDateColumn = column === 'oldest' || column === 'newest';
        const currentlyDate = prev.sortBy === 'oldest' || prev.sortBy === 'newest';
        const sameColumn = isDateColumn ? currentlyDate : prev.sortBy === column;
        if (sameColumn) {
          return { ...prev, sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc' };
        }
        return { ...prev, sortBy: column, sortDirection: 'asc' };
      });
    },
    []
  );

  const showEmptyQueue =
    filteredItems.length === 0 &&
    (filters.status.length > 0 ||
      filters.clinicianIds.length > 0 ||
      filters.documentTypes.length > 0 ||
      filters.search !== '');

  const showNoUnsigned =
    filteredItems.length === 0 &&
    filters.status.length === 0 &&
    filters.clinicianIds.length === 0 &&
    filters.documentTypes.length === 0 &&
    filters.search === '';

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState<string>('50');
  const itemsPerPage = parseInt(rowsPerPage || '50', 10);
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredItems.length]);
  const paginatedItems = useMemo(
    () =>
      filteredItems.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      ),
    [filteredItems, currentPage, itemsPerPage]
  );

  if (loading) {
    return (
      <Stack gap="md">
        <Group gap="md">
          <Loader size="sm" />
          <span>{unsignedEmpty ? 'Loading unsigned notes…' : 'Loading cosign queue…'}</span>
        </Group>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap="md">
        <Alert color="red" title={unsignedEmpty ? 'Error loading unsigned notes' : 'Error loading cosign queue'}>
          {error}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group gap="md">
        <SupervisionFilters
          filters={filters}
          onFiltersChange={setFilters}
          clinicianOptions={clinicianOptions}
          hideClinicianFilter={unsignedEmpty}
          hideStatusFilter={isOwnUnsigned}
          documentTypes={unsignedEmpty ? OWN_UNSIGNED_DOCUMENT_TYPES : undefined}
          statuses={unsignedEmpty && !isOwnUnsigned ? ['draft', 'edits-requested'] : undefined}
        />
      </Group>

      <Paper p="lg" withBorder>
        {showNoUnsigned && unsignedCount === 0 ? (
          <SupervisionEmptyState variant={unsignedEmpty ? 'no-unsigned-notes' : 'no-cosign-notes'} />
        ) : showEmptyQueue ? (
          <SupervisionEmptyState
            variant="no-results"
            onClearFilters={() => setFilters(defaultSupervisionFilters)}
          />
        ) : (
          <>
            <div style={{ overflow: 'auto', minHeight: 240, maxHeight: 'min(420px, 55vh)' }}>
              <CosignNotesTable
                items={paginatedItems}
                onItemClick={handleItemClick}
                sortBy={filters.sortBy}
                sortDirection={filters.sortDirection}
                onSort={handleSort}
                hideClinicianColumn={unsignedEmpty}
              />
            </div>
            <TablePaginationFooter
              totalItems={filteredItems.length}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(v) => setRowsPerPage(v ?? '50')}
            />
          </>
        )}
      </Paper>
    </Stack>
  );
}
